import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Settings, Package, Heart, MessageSquare, LogOut, CheckCircle, Upload, Camera, Search, Trash2, RefreshCw, MapPin, Download, FileText, Video, Image, File, ExternalLink } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// Fix leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function LocationPicker({ position, onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng); }
  });
  return position ? <Marker position={position} /> : null;
}

export default function Profile() {
  const { user, logout, updateUser, refreshUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'info');
  const [products, setProducts] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [myWants, setMyWants] = useState([]);
  const [stats, setStats] = useState({ products: 0, views: 0, sales: 0 });
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [shopAddress, setShopAddress] = useState('');
  const [shopPos, setShopPos] = useState(null);
  const [shopSaving, setShopSaving] = useState(false);
  const [shopMsg, setShopMsg] = useState('');
  const [idFrontPreview, setIdFrontPreview] = useState(null);
  const [idBackPreview, setIdBackPreview] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [idUploadSuccess, setIdUploadSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', province: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');

  const PROVINCES = ['SAN_JOSE', 'ALAJUELA', 'HEREDIA', 'CARTAGO', 'GUANACASTE', 'PUNTARENAS', 'LIMON'];

  useEffect(() => {
    if (user) {
      setIdFrontPreview(user.id_front);
      setIdBackPreview(user.id_back);
      setEditForm({
        name: user.name || '',
        phone: user.phone || '',
        province: user.province || 'SAN_JOSE',
        email: user.email || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user]);

  useEffect(() => {
    if (user && tab === 'products') fetchProducts();
    if (user && tab === 'wishlist') fetchWishlist();
    if (user && tab === 'wants') fetchMyWants();
    if (user && tab === 'location') fetchShopLocation();
  }, [tab, user]);

  const [shopType, setShopType] = useState('NONE');

  const fetchShopLocation = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setShopType(data.shop_type || 'NONE');
      setShopAddress(data.shop_address || '');
      if (data.shop_lat) setShopPos({ lat: data.shop_lat, lng: data.shop_lng });
    } catch {}
  };

  const saveShopLocation = async () => {
    setShopSaving(true);
    setShopMsg('');
    try {
      await api.put('/profile/shop', {
        shop_type: shopType,
        shop_address: shopAddress,
        shop_lat: shopPos?.lat || null,
        shop_lng: shopPos?.lng || null,
      });
      setShopMsg('✅ Guardado correctamente');
    } catch (e) {
      setShopMsg('❌ ' + (e.response?.data?.error || 'Error'));
    } finally {
      setShopSaving(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await api.get('/my/products');
      setProducts(data);
      setStats({
        products: data.length,
        views: data.reduce((s, p) => s + (p.views || 0), 0),
        sales: data.filter(p => p.status === 'SOLD').length
      });
    } catch {}
  };

  const fetchWishlist = async () => {
    try {
      const { data } = await api.get('/wishlist');
      setWishlist(data);
    } catch {}
  };

  const fetchMyWants = async () => {
    try {
      const { data } = await api.get('/my/wants');
      setMyWants(data.wants || []);
    } catch {
      setMyWants([]);
    }
  };

  const deleteWant = async (id) => {
    if (!confirm('¿Eliminar esta solicitud?')) return;
    try {
      await api.delete(`/my/wants/${id}`);
      setMyWants(prev => prev.filter(w => w.id !== id));
    } catch {
      alert('Error al eliminar');
    }
  };

  const reactivateWant = async (id) => {
    if (!confirm('¿Reactivar esta solicitud?')) return;
    try {
      await api.post(`/my/wants/${id}/reactivate`);
      setMyWants(prev => prev.map(w => w.id === id ? { ...w, status: 'ACTIVE' } : w));
    } catch (err) {
      alert(err.response?.data?.error || 'Error al reactivar');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSaveProfile = async () => {
    if (!editForm.name?.trim()) {
      setProfileMsg('❌ El nombre es requerido');
      return;
    }
    if (!editForm.email?.trim() || !editForm.email.includes('@')) {
      setProfileMsg('❌ El correo electrónico es requerido');
      return;
    }
    setSavingProfile(true);
    setProfileMsg('');
    try {
      await api.put('/auth/me', {
        name: editForm.name,
        phone: editForm.phone || null,
        province: editForm.province,
        email: editForm.email
      });
      setProfileMsg('✅ Perfil actualizado');
      setEditMode(false);
      await refreshUser();
    } catch (e) {
      setProfileMsg('❌ ' + (e.response?.data?.error || 'Error al guardar'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleIdUpload = async () => {
    if (!idFront && !idBack) return;
    
    setUploadingId(true);
    try {
      const formData = new FormData();
      if (idFront) formData.append('id_front', idFront);
      if (idBack) formData.append('id_back', idBack);
      
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/auth/upload-id`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al subir documentos');
      
      setIdUploadSuccess(true);
      const { data: updatedUser } = await api.get('/auth/me');
      if (updatedUser) {
        updateUser(updatedUser);
      }
    } catch (err) {
      alert(err.message || 'Error al subir documentos');
    } finally {
      setUploadingId(false);
    }
  };

  if (authLoading || !user) return null;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-secondary to-dark text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-primary text-white rounded-full flex items-center justify-center text-3xl font-bold">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{user.name}</h1>
              <p className="text-gray-300">{user.email}</p>
              <div className="flex items-center gap-2 mt-2">
                {user.is_verified ? (
                  <span className="bg-green-500 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Verificado
                  </span>
                ) : (
                  <span className="bg-yellow-500 px-3 py-1 rounded-full text-sm">Pendiente verificación</span>
                )}
                <span className={`px-3 py-1 rounded-full text-sm ${
                  user.subscription_plan === 'PREMIUM' ? 'bg-purple-500' :
                  user.subscription_plan === 'PRO' ? 'bg-blue-500' : 'bg-gray-500'
                }`}>
                  {user.subscription_plan}
                </span>
              </div>
            </div>
            <button 
              onClick={() => setEditMode(!editMode)}
              className="ml-auto bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition"
            >
              <Settings className="w-4 h-4" />
              {editMode ? 'Cancelar' : 'Editar Perfil'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex overflow-x-auto">
            {[
              { id: 'info', icon: Settings, label: 'Perfil' },
              { id: 'products', icon: Package, label: 'Mis Productos' },
              { id: 'wants', icon: Search, label: 'Mis Solicitudes' },
              { id: 'wishlist', icon: Heart, label: 'Favoritos' },
              { id: 'messages', icon: MessageSquare, label: 'Mensajes' },
              ...(user?.role === 'SELLER' || user?.role === 'ADMIN' ? [{ id: 'location', icon: MapPin, label: 'Tienda' }] : []),
              { id: 'downloads', icon: Download, label: 'Descargas' }
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-2 px-4 py-4 border-b-2 whitespace-nowrap transition ${
                  tab === id ? 'border-primary text-primary' : 'border-transparent text-gray-500'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {tab === 'info' && (
          <div className="space-y-6">
            {/* Edit Profile Form */}
            {editMode && (
              <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-primary/20">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Editar Perfil
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Nombre completo *</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Tu nombre"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Correo electrónico *</label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="tu@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Teléfono</label>
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Ej: 8888-8888"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Provincia</label>
                    <select
                      value={editForm.province}
                      onChange={(e) => setEditForm({...editForm, province: e.target.value})}
                      className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      {PROVINCES.map(p => (
                        <option key={p} value={p}>{p.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                  
                  {profileMsg && (
                    <p className={`text-sm font-medium ${profileMsg.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>
                      {profileMsg}
                    </p>
                  )}
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveProfile}
                      disabled={savingProfile}
                      className="bg-primary text-white px-6 py-3 rounded-lg font-bold hover:bg-primary-dark disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingProfile ? 'Guardando...' : '💾 Guardar Cambios'}
                    </button>
                    <button
                      onClick={() => { setEditMode(false); setProfileMsg(''); }}
                      className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-300"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <p className="text-3xl font-bold text-primary">{stats.products}</p>
                <p className="text-sm text-gray-500">Productos</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <p className="text-3xl font-bold text-primary">{stats.views}</p>
                <p className="text-sm text-gray-500">Vistas</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                <p className="text-3xl font-bold text-primary">{stats.sales}</p>
                <p className="text-sm text-gray-500">Vendidos</p>
              </div>
            </div>

            {/* Verify Seller - Cedula Upload */}
            {user.role !== 'ADMIN' && (
              <div className={`rounded-xl p-6 border-2 ${
                user.is_verified 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className={`font-semibold mb-2 ${user.is_verified ? 'text-green-800' : 'text-yellow-800'}`}>
                      {user.is_verified ? '✅ Cuenta Verificada' : '📋 Verificación de Identidad'}
                    </h3>
                    <p className={`text-sm ${user.is_verified ? 'text-green-700' : 'text-yellow-700'}`}>
                      {user.is_verified 
                        ? 'Tu identidad ha sido verificada. Tienes el badge de vendedor verificado.'
                        : 'Sube las fotos de tu cédula (anverso y reverso) para verificar tu identidad.'
                      }
                    </p>
                  </div>
                </div>

                {idUploadSuccess && (
                  <div className="bg-green-100 text-green-700 p-3 rounded-lg mb-4 text-sm">
                    ✅ ¡Documentos subidos correctamente!
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">📷 Frente de Cédula *</label>
                    <div 
                      className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition ${
                        idFront ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-primary'
                      }`}
                      onClick={() => document.getElementById('id_front_input').click()}
                    >
                      {idFrontPreview ? (
                        <div className="relative">
                          <img src={idFrontPreview} alt="Frente cédula" className="max-h-32 mx-auto rounded-lg" />
                          <span className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">✓</span>
                        </div>
                      ) : (
                        <>
                          <Camera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Clic para subir</p>
                        </>
                      )}
                      <input 
                        id="id_front_input"
                        type="file" 
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setIdFront(file);
                            setIdFrontPreview(URL.createObjectURL(file));
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">📷 Reverso de Cédula *</label>
                    <div 
                      className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition ${
                        idBack ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-primary'
                      }`}
                      onClick={() => document.getElementById('id_back_input').click()}
                    >
                      {idBackPreview ? (
                        <div className="relative">
                          <img src={idBackPreview} alt="Reverso cédula" className="max-h-32 mx-auto rounded-lg" />
                          <span className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">✓</span>
                        </div>
                      ) : (
                        <>
                          <Camera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">Clic para subir</p>
                        </>
                      )}
                      <input 
                        id="id_back_input"
                        type="file" 
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setIdBack(file);
                            setIdBackPreview(URL.createObjectURL(file));
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {(!user.id_front || !user.id_back) && (
                  <button 
                    onClick={handleIdUpload}
                    disabled={!idFront && !idBack || uploadingId}
                    className={`mt-4 flex items-center justify-center gap-2 w-full py-3 rounded-xl font-medium transition ${
                      !idFront && !idBack 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary-dark'
                    }`}
                  >
                    {uploadingId ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Subir Documentos
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            <button onClick={handleLogout} className="flex items-center gap-2 text-red-500 hover:bg-red-50 px-4 py-2 rounded-lg">
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          </div>
        )}

        {tab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Mis Productos</h2>
              <Link to="/seller/products/new" className="bg-primary text-white px-4 py-2 rounded-full text-sm">
                + Publicar
              </Link>
            </div>
            {products.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">📦</div>
                <p className="text-gray-500">No tienes productos publicados</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {products.map(product => (
                  <div key={product.id} className="bg-white rounded-xl p-4 shadow-sm flex gap-4">
                    <img 
                      src={product.images?.[0]?.url || 'https://via.placeholder.com/100x100'} 
                      alt=""
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold line-clamp-1">{product.title}</h3>
                      <p className="text-primary font-bold">₡{product.price.toLocaleString('es-CR')}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        product.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                        product.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {product.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'wants' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Mis Solicitudes</h2>
              <Link to="/wants/new" className="bg-primary text-white px-4 py-2 rounded-full text-sm">
                + Nueva solicitud
              </Link>
            </div>
            {myWants.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">🔍</div>
                <p className="text-gray-500 mb-4">No has creado solicitudes de piezas</p>
                <Link to="/wants/new" className="bg-primary text-white px-6 py-2 rounded-full inline-block">
                  Crear mi primera solicitud
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {myWants.map(want => (
                  <div key={want.id} className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center text-2xl">
                      🔍
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{want.title}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="font-bold text-primary">
                          ₡{want.budget_min?.toLocaleString('es-CR')}
                          {want.budget_max && ` - ₡${want.budget_max.toLocaleString('es-CR')}`}
                        </span>
                        <span>•</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          want.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                          want.status === 'EXPIRED' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {want.status === 'ACTIVE' ? 'Activa' : want.status}
                        </span>
                      </div>
                    </div>
                    <Link to={`/wants/${want.id}`} className="text-primary hover:bg-primary/10 px-3 py-2 rounded-lg text-sm font-medium">
                      Ver
                    </Link>
                    <button onClick={() => deleteWant(want.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'wishlist' && (
          <div>
            <h2 className="text-xl font-bold mb-6">Mis Favoritos</h2>
            {wishlist.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">❤️</div>
                <p className="text-gray-500">No tienes favoritos</p>
                <Link to="/products" className="text-primary font-medium mt-4 inline-block">
                  Explorar productos
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {wishlist.map(product => (
                  <div key={product.id} className="bg-white rounded-xl overflow-hidden shadow-sm relative group">
                    <Link to={`/products/${product.id}`}>
                      <img 
                        src={product.images?.[0]?.url || 'https://via.placeholder.com/200x200'} 
                        alt={product.title}
                        className="w-full aspect-square object-cover"
                      />
                      <div className="p-4">
                        <h3 className="font-semibold line-clamp-1">{product.title}</h3>
                        <p className="text-primary font-bold">₡{product.price.toLocaleString('es-CR')}</p>
                      </div>
                    </Link>
                    <button
                      onClick={async () => { await api.delete(`/wishlist/${product.id}`); setWishlist(w => w.filter(p => p.id !== product.id)); }}
                      className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition"
                    >
                      <Heart className="w-4 h-4 fill-primary text-primary" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'messages' && (
          <div className="text-center py-12 bg-white rounded-xl">
            <div className="text-6xl mb-4">💬</div>
            <p className="text-gray-500 mb-4">Tus conversaciones</p>
            <Link to="/messages" className="bg-primary text-white px-4 py-2 rounded-full">
              Ver mensajes
            </Link>
          </div>
        )}

        {tab === 'location' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-1 flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /> Taller / Repuestera</h2>
            <p className="text-gray-500 text-sm mb-4">Configura tu tipo de negocio y ubicación.</p>

            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Tipo de negocio</label>
              <div className="flex gap-3">
                {[['NONE','No mostrar'],['TALLER','🔧 Taller'],['REPUESTERA','🛞 Repuestera'],['GRUA','🚛 Grúa']].map(([val, label]) => (
                  <button key={val} onClick={() => setShopType(val)}
                    className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition ${shopType === val ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {shopType !== 'NONE' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-bold mb-1">Dirección</label>
                  <input type="text" value={shopAddress} onChange={e => setShopAddress(e.target.value)}
                    placeholder="Ej: 100m norte del parque central, San José"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="rounded-lg overflow-hidden border border-gray-300 mb-4" style={{height: '380px'}}>
                  <MapContainer center={shopPos || { lat: 9.9281, lng: -84.0907 }} zoom={13} style={{height: '100%', width: '100%'}}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <LocationPicker position={shopPos} onPick={setShopPos} />
                  </MapContainer>
                </div>
              </>
            )}

            {shopMsg && (
              <p className={`text-sm font-medium mb-3 ${shopMsg.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>{shopMsg}</p>
            )}
            <button onClick={saveShopLocation} disabled={shopSaving}
              className="bg-primary text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-primary-dark disabled:opacity-50">
              {shopSaving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        )}

        {tab === 'downloads' && <DownloadList />}
      </div>
    </div>
  );
}

// Convert Google Drive share link to direct download link
function convertGoogleDriveLink(url) {
  if (!url) return url;
  const driveMatch = url.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/);
  if (driveMatch) {
    return `https://drive.google.com/uc?export=download&id=${driveMatch[1]}`;
  }
  return url;
}

function DownloadList() {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDownloads();
  }, []);

  const loadDownloads = async () => {
    try {
      const res = await fetch('/api/downloads');
      const data = await res.json();
      setDownloads(data.downloads || []);
    } catch (err) {
      console.error('Error loading downloads:', err);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'PDF': return <FileText className="w-6 h-6" />;
      case 'VIDEO': return <Video className="w-6 h-6" />;
      case 'IMAGE': return <Image className="w-6 h-6" />;
      default: return <File className="w-6 h-6" />;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'PDF': return 'bg-red-100 text-red-600';
      case 'VIDEO': return 'bg-purple-100 text-purple-600';
      case 'IMAGE': return 'bg-green-100 text-green-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const isGoogleDrive = (url) => url && url.includes('drive.google.com');

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="mt-4 text-gray-500">Cargando...</p>
      </div>
    );
  }

  if (downloads.length === 0) {
    return (
      <div className="bg-white rounded-xl p-12 text-center shadow-sm">
        <Download className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <p className="text-xl font-semibold text-gray-500">No hay descargas disponibles</p>
        <p className="text-gray-400 mt-2">El administrador agregará contenido pronto</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {downloads.map((item) => {
        const isDrive = isGoogleDrive(item.url);
        const downloadUrl = isDrive ? convertGoogleDriveLink(item.url) : item.url;
        
        return (
          <a
            key={item.id}
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition border border-gray-100 flex items-center gap-4 group"
          >
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${getColor(item.file_type)}`}>
              {getIcon(item.file_type)}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg text-gray-900 group-hover:text-primary transition truncate">
                {item.title}
              </h3>
              {item.description && (
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.description}</p>
              )}
              {isDrive && (
                <span className="text-xs text-blue-600 mt-1 inline-block">📁 Desde Google Drive</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition">
              <span className="text-sm font-medium">Descargar</span>
              <ExternalLink className="w-5 h-5" />
            </div>
          </a>
        );
      })}
    </div>
  );
}
